import React, { useMemo } from 'react';
import {
  Paper,
  Box,
  Typography,
  Alert,
  Button,
  Grid,
  TextField,
  Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import moment from 'moment';

interface SecurityData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface SecurityTabProps {
  profileData: Record<string, any>;
  securityData: SecurityData;
  onSecurityDataChange: (data: SecurityData) => void;
  onChangePassword: () => void;
  onDeleteAccount: () => void;
  onCancelDeletion: () => void;
}

export default function SecurityTab({
  profileData,
  securityData,
  onSecurityDataChange,
  onChangePassword,
  onDeleteAccount,
  onCancelDeletion,
}: SecurityTabProps) {
  const passwordRules = useMemo(() => {
    const p = securityData.newPassword || '';
    return {
      length: p.length >= 8,
      uppercase: /[A-Z]/.test(p),
      lowercase: /[a-z]/.test(p),
      number: /[0-9]/.test(p),
      special: /[^A-Za-z0-9]/.test(p),
    };
  }, [securityData.newPassword]);

  const passwordValid = Object.values(passwordRules).every(Boolean);
  const passwordsMatch = securityData.newPassword === securityData.confirmPassword && securityData.newPassword !== '';
  const canSubmit =
    securityData.currentPassword &&
    securityData.newPassword &&
    securityData.confirmPassword &&
    passwordValid &&
    passwordsMatch &&
    !profileData.archived;
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
        Security Settings
      </Typography>

      {/* Account Deletion Status */}
      {profileData.archived && (
        <Alert
          severity="warning"
          sx={{ mb: 4 }}
          icon={<WarningIcon />}
        >
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Account Scheduled for Deletion
            </Typography>
            <Typography variant="body2" gutterBottom>
              Your account is set to be permanently deleted on{' '}
              <strong>
                {moment(profileData.archived).add(30, 'days').format('MMMM D, YYYY [at] h:mm A')}
              </strong>
            </Typography>
            <Typography variant="overline" display="block" sx={{ mt: 1 }}>
              Deletion requested: {moment(profileData.archived).format('MMMM D, YYYY [at] h:mm A')}
            </Typography>
            <Typography variant="caption" display="block">
              *deletions are scheduled 30 days from the last date of paid access
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              sx={{ mt: 2 }}
              onClick={onCancelDeletion}
            >
              Cancel Deletion
            </Button>
          </Box>
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Change Password
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={securityData.currentPassword}
              onChange={(e) =>
                onSecurityDataChange({
                  ...securityData,
                  currentPassword: e.target.value,
                })
              }
              disabled={!!profileData.archived}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={securityData.newPassword}
              onChange={(e) =>
                onSecurityDataChange({
                  ...securityData,
                  newPassword: e.target.value,
                })
              }
              disabled={!!profileData.archived}
              helperText="Password must be at least 8 characters long and include uppercase letters, lowercase letters, numbers, and special characters."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={securityData.confirmPassword}
              onChange={(e) =>
                onSecurityDataChange({
                  ...securityData,
                  confirmPassword: e.target.value,
                })
              }
              disabled={!!profileData.archived}
              helperText={
                securityData.newPassword === ""
                  ? ""
                  : securityData.newPassword === securityData.confirmPassword
                    ? "Passwords match."
                    : "Passwords do not match."
              }
              error={securityData.newPassword !== "" && securityData.newPassword !== securityData.confirmPassword}
            />
          </Grid>

          {/* Password Requirements */}
          {securityData.newPassword && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1 }}>
                  Password Requirements
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon sx={{ color: passwordRules.length ? 'success.main' : 'text.disabled', fontSize: '1.2rem' }} />
                    <Typography variant="caption" color={'text.primary'}>
                      At least 8 characters
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon sx={{ color: passwordRules.uppercase ? 'success.main' : 'text.disabled', fontSize: '1.2rem' }} />
                    <Typography variant="caption" color={'text.primary'}>
                      Uppercase letter (A-Z)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon sx={{ color: passwordRules.lowercase ? 'success.main' : 'text.disabled', fontSize: '1.2rem' }} />
                    <Typography variant="caption" color={'text.primary'}>
                      Lowercase letter (a-z)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon sx={{ color: passwordRules.number ? 'success.main' : 'text.disabled', fontSize: '1.2rem' }} />
                    <Typography variant="caption" color={'text.primary'}>
                      Number (0-9)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon sx={{ color: passwordRules.special ? 'success.main' : 'text.disabled', fontSize: '1.2rem' }} />
                    <Typography variant="caption" color={'text.primary'}>
                      Special character (e.g. !@#$%)
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={onChangePassword}
              disabled={!canSubmit}
            >
              Update Password
            </Button>
            {profileData.archived && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                Password changes are disabled for accounts scheduled for deletion
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      <>
        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom color="error">
            Danger Zone
          </Typography>

          {profileData.archived ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Your account deletion is already scheduled. Cancel the deletion above if you wish to keep your account.
              </Typography>
            </Alert>
          ) : (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                Once you delete your account, there is no going back. Please be certain.
              </Alert>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onDeleteAccount}
              >
                Delete Account
              </Button>
            </>
          )}
        </Box>
      </>
    </Paper>
  );
}
